/**
 * UsersPanel - User account management
 * Handles: Create, edit, disable users with role assignments
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Ban, UserPlus, ChevronUp, ChevronDown, Filter, RotateCcw, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useUserManagement } from '@/hooks/useUserManagement';

// Predefined colors for drivers
const DRIVER_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
];

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'admin':
      // Charcoal grey — admin is the "system / privileged" role, kept neutral.
      return 'bg-stone-700 text-stone-100';
    case 'driver':
      return 'bg-emerald-100 text-emerald-700';
    case 'designer':
      // Inherits the previous admin purple — designer is the creative role
      // and the workspace surfaces (My Workspace header, etc.) are purple-tinted.
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-stone-100 text-stone-700';
  }
};

const emptyForm = () => ({ username: '', password: '', name: '', roles: ['admin'], color: '#3B82F6', phone_number: '' });

const UsersWorkspace = ({ model }) => {
  const { controller, users, currentUser, includeDisabled: showDisabled } = model;
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, replaceForm] = useState(emptyForm);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);
  const [reactivatePassword, replaceReactivatePassword] = useState('');
  const [resettingId, setResettingId] = useState(null);
  const [resetPassword, replaceResetPassword] = useState('');
  const [feedback, setFeedback] = useState('');
  const mounted = useRef(false);
  const editor = useRef({ revision: 0 });
  const reset = useRef({ revision: 0 });
  const reactivation = useRef({ revision: 0 });
  const activity = useRef(0);
  const submitting = useRef(false);
  const submittedEditor = useRef(null);
  const submittedReactivation = useRef(null);
  const busy = model.pending || !model.writable;

  useEffect(() => {
    mounted.current = true;
    const retireActivity = () => { ++activity.current; };
    return () => {
      mounted.current = false;
      editor.current = { revision: 0 };
      reset.current = { revision: 0 };
      reactivation.current = { revision: 0 };
      retireActivity();
    };
  }, []);
  const owns = () => mounted.current && controller.owns();
  const setForm = value => { ++editor.current.revision; replaceForm(value); };
  const setResetPassword = value => { ++reset.current.revision; replaceResetPassword(value); };
  const setReactivatePassword = value => { ++reactivation.current.revision; replaceReactivatePassword(value); };
  const resetForm = () => {
    ++activity.current; editor.current = { revision: 0 };
    replaceForm(emptyForm()); setShowForm(false); setEditingId(null);
  };
  const openReset = id => {
    ++activity.current; reset.current = { revision: 0 };
    setResettingId(id); replaceResetPassword(''); if (id) setFeedback('');
  };
  const openReactivate = id => {
    ++activity.current; reactivation.current = { revision: 0 };
    setReactivatingId(id); replaceReactivatePassword(''); if (id) setFeedback('');
  };
  const showResult = (result, accepted) => {
    if (result.kind === 'accepted') { setFeedback(accepted); toast.success(accepted); }
    else if (result.kind === 'unknown' || result.kind === 'rejected') setFeedback(result.message);
  };
  const toggleRole = role => {
    const roles = form.roles || [];
    const next = roles.includes(role) ? roles.filter(value => value !== role) : [...roles, role];
    if (!next.length) { toast.error('User must have at least one role'); return; }
    setForm({ ...form, roles: next });
  };
  const handleEdit = user => {
    if (!owns()) return;
    ++activity.current; editor.current = { revision: 0 }; setFeedback('');
    replaceForm(user ? {
      username: user.username, password: '', name: user.name, roles: [...user.roles],
      color: user.color || '#3B82F6', phone_number: user.phone_number || '',
    } : emptyForm());
    setEditingId(user?.id || null); setShowForm(true);
  };
  const handleSave = async () => {
    if (!owns() || controller.blocked() || submitting.current) return;
    if (!form.username || !form.name || (!editingId && !form.password)) {
      toast.error('Username, password, and name are required'); return;
    }
    if (!form.roles?.length) { toast.error('User must have at least one role'); return; }
    const opening = editor.current; const revision = opening.revision; const id = editingId;
    let payload = { username: form.username, name: form.name, roles: [...form.roles] };
    if (!id) payload.password = form.password;
    if (form.roles.includes('driver')) { payload.color = form.color; payload.phone_number = form.phone_number; }
    submitting.current = true; setFeedback('');
    if (!id) submittedEditor.current = opening;
    const request = controller.mutate(id ? 'update' : 'create', id, payload);
    payload = null;
    replaceForm(current => ({ ...current, password: '' }));
    const result = await request;
    submitting.current = false;
    if (submittedEditor.current === opening) submittedEditor.current = null;
    if (!owns() || editor.current !== opening) return;
    showResult(result, opening.revision !== revision
      ? 'Submitted user details saved. Later edits are still unsaved.'
      : id ? 'User updated' : 'User created');
    if (result.kind === 'accepted') {
      if (!id) {
        setEditingId(result.user.id);
      }
      if (opening.revision === revision) resetForm();
    }
  };
  const handleDisable = async user => {
    if (!owns() || controller.blocked() || user.id === currentUser?.id) return;
    const context = activity.current;
    if (!window.confirm(`Disable "${user.username}"? They won't be able to log in until reactivated.`)) return;
    if (!owns() || context !== activity.current || controller.blocked()) return;
    const result = await controller.mutate('disable', user.id);
    if (owns() && context === activity.current) showResult(result, 'Account disabled');
  };
  const handlePassword = async (kind, user) => {
    if (!owns() || controller.blocked()) return;
    const ref = kind === 'reset' ? reset : reactivation;
    const target = kind === 'reset' ? resettingId : reactivatingId;
    if (target !== user.id) return;
    let password = kind === 'reset' ? resetPassword : reactivatePassword;
    if (!password.trim()) { toast.error('A new password is required'); return; }
    const opening = ref.current; const revision = opening.revision;
    setFeedback('');
    if (kind === 'reactivate') submittedReactivation.current = opening;
    const request = controller.mutate(kind, user.id, password);
    password = '';
    if (kind === 'reset') replaceResetPassword('');
    else replaceReactivatePassword('');
    const result = await request;
    if (submittedReactivation.current === opening) submittedReactivation.current = null;
    if (!owns() || ref.current !== opening) return;
    showResult(result, opening.revision !== revision
      ? 'Submitted account change saved. The later password entry has not been submitted.'
      : kind === 'reset' ? `Password reset for ${user.name}` : `${user.name} reactivated`);
    if (result.kind === 'accepted' && opening.revision === revision) {
      if (kind === 'reset') openReset(null);
      else openReactivate(null);
    }
  };
  const handleResetPassword = user => handlePassword('reset', user);
  const handleReactivate = user => handlePassword('reactivate', user);
  const toggleDisabled = () => {
    if (!owns()) return;
    openReactivate(null); openReset(null); controller.filter(!showDisabled);
  };
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="h-3 w-3 opacity-0 group-hover:opacity-40" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="h-3 w-3 text-stone-700" />
      : <ChevronDown className="h-3 w-3 text-stone-700" />;
  };

  // Collect all unique roles for the filter dropdown
  const allRoles = [...new Set(users.flatMap(u => u.roles || []))].sort();

  // Apply filter then sort
  let displayUsers = showDisabled
    ? users.filter(u => !!u.disabled_at)
    : users.filter(u => !u.disabled_at);
  if (roleFilter !== 'all') {
    displayUsers = displayUsers.filter(u => u.roles?.includes(roleFilter));
  }
  if (sortField) {
    displayUsers = [...displayUsers].sort((a, b) => {
      const valA = (a[sortField] || '').toLowerCase();
      const valB = (b[sortField] || '').toLowerCase();
      return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  }

  return (
    <div className="space-y-6" data-testid="users-section" onClick={(e) => {
      // Close role dropdown when clicking outside it
      if (!e.target.closest('[data-testid="role-filter-btn"]') && !e.target.closest('[data-testid^="role-filter-"]')) {
        setShowRoleDropdown(false);
      }
    }}>
      {model.connectionError && <p role="alert">The backend connection changed. Reload before managing users.</p>}
      {model.state === 'loading' && <p role="status">Loading users...</p>}
      {model.state === 'unavailable' && <p role="alert">{model.known ? 'Showing last-known users. The current user list could not be loaded.' : 'The user list could not be loaded.'}</p>}
      {model.pending && <p role="status">An account change is still settling. Further changes are unavailable until it settles.</p>}
      {!model.writable && <p role="status">Account changes require an online administrator session.</p>}
      {feedback && <p role="status" data-testid="user-feedback">{feedback}</p>}
      <Button variant="outline" size="sm" onClick={controller.refresh} data-testid="users-refresh">Refresh users</Button>
      {model.state === 'available' && displayUsers.length === 0 && <p>No users in this view.</p>}
      {/* Toolbar */}
      <div className="flex items-center justify-end">
        <button
          onClick={toggleDisabled}
          className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${
            showDisabled
              ? 'bg-stone-100 border-stone-300 text-stone-700'
              : 'border-stone-200 text-stone-400 hover:text-stone-600'
          }`}
          data-testid="toggle-disabled-btn"
        >
          <Ban className="h-3.5 w-3.5" />
          {showDisabled ? 'Hiding disabled' : 'Show disabled'}
        </button>
      </div>

      {/* User table - horizontally scrollable on mobile */}
      <div className="border border-stone-200 rounded-lg overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200">
              <th
                className="text-left px-4 py-2.5 text-xs font-semibold text-stone-400 uppercase tracking-wider cursor-pointer select-none group hover:text-stone-600 transition-colors"
                onClick={() => handleSort('name')}
                data-testid="sort-name"
              >
                <span className="inline-flex items-center gap-1">
                  Name <SortIcon field="name" />
                </span>
              </th>
              <th
                className="text-left px-4 py-2.5 text-xs font-semibold text-stone-400 uppercase tracking-wider cursor-pointer select-none group hover:text-stone-600 transition-colors"
                onClick={() => handleSort('username')}
                data-testid="sort-username"
              >
                <span className="inline-flex items-center gap-1">
                  Username <SortIcon field="username" />
                </span>
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-400 uppercase tracking-wider">Password</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-400 uppercase tracking-wider">Map Color</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-400 uppercase tracking-wider">
                <div className="relative inline-block">
                  <button
                    onClick={() => setShowRoleDropdown(prev => !prev)}
                    className={`inline-flex items-center gap-1 hover:text-stone-600 transition-colors ${roleFilter !== 'all' ? 'text-stone-700' : ''}`}
                    data-testid="role-filter-btn"
                  >
                    Role
                    <Filter className={`h-3 w-3 ${roleFilter !== 'all' ? 'text-primary' : ''}`} />
                    {roleFilter !== 'all' && (
                      <span className="ml-1 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full capitalize">{roleFilter}</span>
                    )}
                  </button>
                  {showRoleDropdown && (
                    <div className="absolute left-0 top-full mt-1 z-10 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[120px]">
                      <button
                        onClick={() => { setRoleFilter('all'); setShowRoleDropdown(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50 ${roleFilter === 'all' ? 'font-semibold text-stone-900' : 'text-stone-600'}`}
                        data-testid="role-filter-all"
                      >
                        All Roles
                      </button>
                      {allRoles.map(role => (
                        <button
                          key={role}
                          onClick={() => { setRoleFilter(role); setShowRoleDropdown(false); }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50 capitalize ${roleFilter === role ? 'font-semibold text-stone-900' : 'text-stone-600'}`}
                          data-testid={`role-filter-${role}`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </th>
              <th className="w-24 px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {displayUsers.map((u) => {
              const isDisabled = !!u.disabled_at;
              return (
                <tr
                  key={u.id}
                  className={`border-b border-stone-100 last:border-b-0 ${isDisabled ? 'opacity-50' : ''}`}
                  data-testid={`user-row-${u.username}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${isDisabled ? 'text-stone-400' : 'text-stone-900'}`}>{u.name}</span>
                      {isDisabled && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-stone-100 text-stone-400 uppercase tracking-wide">
                          Disabled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-stone-700">{u.username}</td>
                  <td className="px-4 py-3">
                    {isDisabled ? (
                      <span className="font-mono text-stone-700">—</span>
                    ) : resettingId === u.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                          placeholder="New password"
                          className="h-7 text-xs w-28"
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && handleResetPassword(u)}
                          data-testid={`reset-password-input-${u.username}`}
                        />
                        <Button
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={busy} onClick={() => handleResetPassword(u)}
                          data-testid={`reset-password-confirm-${u.username}`}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2"
                          onClick={() => openReset(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-stone-400 hover:text-stone-700 gap-1"
                        onClick={() => openReset(u.id)}
                        data-testid={`user-reset-password-${u.username}`}
                      >
                        <KeyRound className="h-3.5 w-3.5" />
                        Reset Password
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.roles?.includes('driver') && u.color && (
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: u.color }}
                        title={u.color}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {!u.roles.length && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-700"
                          title="Assign a valid role to restore access."
                        >
                          Unassigned — access blocked
                        </span>
                      )}
                      {u.roles.map((role) => (
                        <span
                          key={role}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${isDisabled ? 'bg-stone-100 text-stone-400' : getRoleBadgeClass(role)}`}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isDisabled ? (
                        reactivatingId === u.id ? (
                          <>
                            <Input
                              value={reactivatePassword}
                              disabled={submittedReactivation.current === reactivation.current}
                              onChange={(e) => {
                                if (submittedReactivation.current !== reactivation.current) setReactivatePassword(e.target.value);
                              }}
                              placeholder="New password"
                              className="h-7 text-xs w-28"
                              autoFocus
                              onKeyDown={(e) => e.key === 'Enter' && handleReactivate(u)}
                              data-testid={`reactivate-password-${u.username}`}
                            />
                            <Button
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={busy} onClick={() => handleReactivate(u)}
                              data-testid={`reactivate-confirm-${u.username}`}
                            >
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => openReactivate(null)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-stone-400 hover:text-emerald-600 gap-1"
                            onClick={() => openReactivate(u.id)}
                            data-testid={`user-reactivate-${u.username}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Reactivate
                          </Button>
                        )
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-stone-400 hover:text-stone-700"
                            onClick={() => handleEdit(u)}
                            data-testid={`user-edit-${u.username}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 px-2 text-stone-400 hover:text-red-600 ${u.id === currentUser?.id ? 'invisible' : ''}`}
                            disabled={busy || !model.known} onClick={() => handleDisable(u)}
                            data-testid={`user-disable-${u.username}`}
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add/Edit form */}
      {showForm ? (
        <div className="border border-stone-200 rounded-lg p-4 space-y-4 bg-white" data-testid="user-form">
          <h3 className="text-sm font-semibold text-stone-700">
            {editingId ? 'Edit User' : 'New User'}
          </h3>
          
          {/* Basic fields */}
          <div className={`grid gap-3 ${editingId ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Diana"
                data-testid="user-form-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Username</Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="diana"
                data-testid="user-form-username"
              />
            </div>
            {!editingId && (
              <div className="space-y-1">
                <Label className="text-xs">Password</Label>
                <Input
                  value={form.password}
                  disabled={submittedEditor.current === editor.current}
                  onChange={(e) => {
                    if (submittedEditor.current !== editor.current) setForm({ ...form, password: e.target.value });
                  }}
                  placeholder="Enter password"
                  data-testid="user-form-password"
                />
              </div>
            )}
          </div>

          {/* Roles checkboxes */}
          <div className="space-y-2 pt-2">
            <Label className="text-xs">Roles</Label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="user-form-role-admin">
                <input
                  type="checkbox"
                  checked={form.roles?.includes('admin') || false}
                  onChange={() => toggleRole('admin')}
                  className="w-4 h-4 rounded border-stone-300 text-stone-700 focus:ring-stone-500"
                />
                <span className="text-sm text-stone-700">Admin</span>
                <span className="text-xs text-stone-400">(Full access)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="user-form-role-driver">
                <input
                  type="checkbox"
                  checked={form.roles?.includes('driver') || false}
                  onChange={() => toggleRole('driver')}
                  className="w-4 h-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-stone-700">Driver</span>
                <span className="text-xs text-stone-400">(Delivery runs)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="user-form-role-designer">
                <input
                  type="checkbox"
                  checked={form.roles?.includes('designer') || false}
                  onChange={() => toggleRole('designer')}
                  className="w-4 h-4 rounded border-stone-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-stone-700">Designer</span>
                <span className="text-xs text-stone-400">(Floral design)</span>
              </label>
            </div>
            <p className="text-xs text-stone-400">Users can have multiple roles. Shop owner? Check both!</p>
          </div>

          {/* Driver-specific fields */}
          {form.roles?.includes('driver') && (
            <div className="border-t pt-3 mt-3">
              <p className="text-xs font-medium text-stone-500 mb-3">Driver Settings</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Map Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {DRIVER_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setForm({ ...form, color })}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          form.color === color ? 'border-stone-900 scale-110' : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        data-testid={`color-${color}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone Number (optional)</Label>
                  <Input
                    value={form.phone_number}
                    onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                    placeholder="702-555-1234"
                    data-testid="user-form-phone"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button size="sm" disabled={busy} onClick={handleSave} data-testid="user-form-save">
              {editingId ? 'Save Changes' : 'Create User'}
            </Button>
            <Button size="sm" variant="ghost" onClick={resetForm} data-testid="user-form-cancel">
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => handleEdit(null)}
          data-testid="user-add-btn"
        >
          <UserPlus className="h-4 w-4" />
          Add User
        </Button>
      )}
    </div>
  );
};

const UsersPanel = () => {
  const model = useUserManagement();
  if (!model.active) return <p role="alert">{model.connectionError
    ? 'The backend connection changed. Reload before managing users.'
    : 'User settings are unavailable for this session.'}</p>;
  return <UsersWorkspace key={model.controller.key} model={model} />;
};

export default UsersPanel;
